// Round 18 — Over-the-Counter (OTC) Medication Authorization.
//
// Single source of truth for the OTC list, the parent-facing wording, and the
// small read/fold helpers. Framework-agnostic: imported by the Step 3 UI, the
// draft/submission services, the tracker writer, the PHI export, the PDF
// generator, AND round18.test.ts — so what is tested is exactly what ships,
// mirroring attestationText.ts (Round 17).
//
// PARENT-FACING WORDING IS DRAFT — pending the approval packet. When the
// approved verbatim text returns, swap OTC_INTRO_TEXT / OTC_ACKNOWLEDGEMENT_TEXT
// (and any list/indication edits) here in ONE place. The "esp. Pepto-Bismol"
// this line used to carry is gone with the footnote below: that item's open
// question is settled, and it is now an ordinary row like any other.
//
// Stable ids: the per-item `id` is the durable key stored on each record, so the
// list can be re-versioned year-to-year without breaking stored answers. The
// visible `name` (active ingredient + brand) is snapshotted alongside the id in
// the PHI export/PDF at submit, so historical records stay accurate if the list
// later changes.

import type {
  OtcAuthorization,
  OtcFollowupAnswer,
  OtcPermission,
  OtcSelection,
} from "./types";

/**
 * Bump when the list or its indications change materially (stored per record).
 * 2027.2 (Round 20): removed the generic "Sunscreen (SPF 30+)" item — a content
 * change, not a cosmetic one, so records written under 2027.1 still resolve to
 * the list that actually included it.
 */
export const OTC_LIST_VERSION = "2027.2";

/** Token replaced with the running program's name at render time. */
export const OTC_PROGRAM_TOKEN = "{PROGRAM}";

/** Substitutes the program name for the {PROGRAM} token in a parent-facing string. */
export function withOtcProgramName(text: string, programName: string): string {
  return text.split(OTC_PROGRAM_TOKEN).join(programName);
}

// Round 35 — `footnote?: string` was removed alongside the Pepto-Bismol
// caution, the only item that ever carried one. Kept as a deliberate deletion
// rather than an unused field: a rendering path no item can trigger reads, on
// the next pass through this file, as "per-item footnotes are a thing we do"
// and invites a second caution to be added here instead of being taken to the
// medical supervisor. Re-adding it is a three-line change if a real one is
// ever approved.
export interface OtcItem {
  id: string;
  name: string;
  use: string;
}

export const OTC_ITEMS: OtcItem[] = [
  { id: "acetaminophen", name: "Acetaminophen (Tylenol)", use: "Pain, headache, fever" },
  { id: "ibuprofen", name: "Ibuprofen (Advil/Motrin)", use: "Pain, inflammation, fever" },
  { id: "calcium_carbonate", name: "Calcium Carbonate (Tums)", use: "Heartburn / upset stomach" },
  { id: "famotidine", name: "Famotidine (Pepcid)", use: "Heartburn / reflux" },
  // Round 35 — the "PENDING medical-advisor confirmation" caution footnote was
  // REMOVED. The question it stood for is settled: Pepto-Bismol stays on the
  // list with the same Yes/No/Call-Me-First controls as every other item and no
  // special app-level treatment. The salicylate judgment (flu-like symptoms and
  // so on) belongs to the onsite RN at the point of administration, not to a
  // parent filling in a form weeks earlier — and a visible marker saying review
  // was still pending had become simply untrue.
  {
    id: "bismuth_subsalicylate",
    name: "Bismuth Subsalicylate (Pepto-Bismol)",
    use: "Upset stomach / diarrhea",
  },
  { id: "loperamide", name: "Loperamide (Imodium)", use: "Diarrhea" },
  { id: "peg3350", name: "Polyethylene Glycol (MiraLAX)", use: "Constipation" },
  { id: "senna", name: "Senna (Senokot/Ex-Lax)", use: "Constipation" },
  { id: "diphenhydramine", name: "Diphenhydramine (Benadryl)", use: "Allergic reactions / itching" },
  { id: "loratadine", name: "Loratadine (Claritin)", use: "Seasonal allergies" },
  { id: "cough_drops", name: "Cough Drops / Throat Lozenges", use: "Sore throat / cough" },
  { id: "saline_nasal", name: "Saline Nasal Spray", use: "Nasal congestion / dryness" },
  { id: "saline_eye", name: "Saline Eye Drops", use: "Eye irritation / dryness" },
  { id: "hydrocortisone", name: "Hydrocortisone 1% Cream", use: "Itching / rashes / insect bites" },
  {
    id: "antibiotic_ointment",
    name: "Antibiotic Ointment (Bacitracin/Polysporin)",
    use: "Minor cuts / scrapes",
  },
  { id: "calamine", name: "Calamine Lotion", use: "Poison ivy / insect bites" },
  // Round 20 — the generic "Sunscreen (SPF 30+)" entry was REMOVED. The
  // dedicated Sunscreen & Insect Repellent Authorization section now carries a
  // specific branded sunscreen with its own consent; keeping a second, generic
  // sunscreen row here created exactly the duplicate-consent risk the medical
  // supervisor flagged for the free-text follow-ups — a parent could answer the
  // two inconsistently. A stray stored "sunscreen" selection on an old draft is
  // silently ignored (every consumer iterates OTC_ITEMS / looks ids up in it).
  { id: "aloe_vera", name: "Aloe Vera Gel", use: "Minor sunburns" },
  { id: "oral_electrolyte", name: "Oral Electrolyte Solution", use: "Dehydration" },
];

// DRAFT — pending approval packet. Swap to verbatim approved text when returned.
export const OTC_INTRO_TEXT = `Please indicate whether you authorize the ${OTC_PROGRAM_TOKEN} medical staff to administer the following over-the-counter medications if medically appropriate. Medications will only be administered by, or under the direction of, authorized medical personnel according to standard dosing guidelines and your child's age, weight, medical history, and allergies.`;

export const OTC_BLANK_DEFAULT_NOTE =
  "Any item left blank will be treated as not authorized (No).";

// Round 20 — reduced from four questions to one, per the program's medical
// supervisor (Mark Ubbens, CRNP): parallel free-text/Yes-No fields alongside the
// per-item table let a parent enter something that contradicts their own
// per-item selections, with no guarantee staff notice the conflict. The
// medication-allergies question is the only one that adds information the table
// cannot carry, so it is the only survivor. The selections table's remaining
// items and wording are unchanged (only the sunscreen row was dropped, above).
export const OTC_QUESTIONS = ["Does your child have any allergies to medications?"];

// Round 20 — simplified: the "clinical/professional judgment" language was
// removed because that authority sits with the prescriber (via the standing
// order), not with the unlicensed/assistive staff administering under it.
export const OTC_ACKNOWLEDGEMENT_TEXT = `I authorize the ${OTC_PROGRAM_TOKEN} medical staff to administer only those medications that I have approved above.`;

/** Intro paragraph with the program name substituted in. */
export function otcIntroText(programName: string): string {
  return withOtcProgramName(OTC_INTRO_TEXT, programName);
}

/** Acknowledgement paragraph with the program name substituted in. */
export function otcAcknowledgementText(programName: string): string {
  return withOtcProgramName(OTC_ACKNOWLEDGEMENT_TEXT, programName);
}

// Round 19 — the OTC follow-up questions became OtcFollowupAnswer (Yes/No +
// conditional required detail) rather than free text.
// Round 20 — three of the four were removed; medAllergies is the only survivor.
// The key list stays an array (not a bare constant) so the loops that drive the
// UI, validation, PDF, and export keep working unchanged over one entry.
export type OtcFollowupKey = "medAllergies";

/** The follow-up question keys, in export/PDF order. */
export const OTC_QUESTION_FIELDS: Array<{ key: OtcFollowupKey; label: string }> = [
  { key: "medAllergies", label: OTC_QUESTIONS[0] },
];

/** A blank (unanswered) follow-up answer. */
export function emptyOtcFollowup(): OtcFollowupAnswer {
  return { answer: null, detail: "" };
}

/**
 * Fold one follow-up answer to a single display/export string:
 *   "Yes — <detail>" (Yes with detail) | "Yes" (Yes, no detail) | "No" | ""
 * (unanswered). Matches the "Yes — <detail>" fold style already used by the
 * Allergies/accommodations exports (§3.4). Shared so the PHI export, the PDF,
 * and the tests all render identically. Note: at submit the question is
 * required, so "" only appears for in-progress/legacy data.
 */
export function otcFollowupFold(a: OtcFollowupAnswer | undefined | null): string {
  if (a?.answer === "Yes") {
    const detail = a.detail?.trim();
    return detail ? `Yes — ${detail}` : "Yes";
  }
  if (a?.answer === "No") return "No";
  return "";
}

/** A blank OTC block (used for defaults and back-fill of pre-Round-18 drafts). */
export function emptyOtcAuthorization(): OtcAuthorization {
  return {
    listVersion: OTC_LIST_VERSION,
    selections: [],
    medAllergies: emptyOtcFollowup(),
    acknowledged: false,
    acknowledgementSignature: null,
    acknowledgedAtUtc: null,
  };
}

/**
 * Read-time coercion (§6): a per-item selection that the parent never answered
 * reads as "no" — the safest administration default. Used by the export, the
 * PDF, and the tracker fold so they all agree.
 */
export function otcPermissionFor(
  otc: OtcAuthorization | undefined,
  id: string
): OtcPermission {
  const sel = otc?.selections?.find((s) => s.id === id);
  if (sel?.permission === "yes" || sel?.permission === "call") return sel.permission;
  return "no";
}

/** Human label for a permission value. */
export function otcPermissionLabel(p: OtcPermission): "Yes" | "No" | "Call First" {
  return p === "yes" ? "Yes" : p === "call" ? "Call First" : "No";
}

/**
 * Short display name for the fold/summary — the brand/parenthetical when present
 * ("Acetaminophen (Tylenol)" -> "Tylenol"), else the full name.
 */
export function otcShortName(item: OtcItem): string {
  const m = /\(([^)]+)\)/.exec(item.name);
  return (m ? m[1] : item.name).trim();
}

/**
 * Compact tracker fold of the parent's EXPLICIT selections (blanks omitted),
 * grouped Yes | Call First | No — e.g.
 *   "Yes: Tylenol, Advil, Tums | Call First: Pepto-Bismol | No: Benadryl".
 * Returns "None authorized" when nothing is authorized (no Yes and no Call
 * First), mirroring the Medications/Allergies inline-fold style. §8.
 */
export function otcTrackerSummary(otc: OtcAuthorization | undefined): string {
  const byId = new Map(OTC_ITEMS.map((it) => [it.id, it]));
  const yes: string[] = [];
  const call: string[] = [];
  const no: string[] = [];
  for (const sel of otc?.selections ?? []) {
    const item = byId.get(sel.id);
    if (!item) continue;
    const short = otcShortName(item);
    if (sel.permission === "yes") yes.push(short);
    else if (sel.permission === "call") call.push(short);
    else if (sel.permission === "no") no.push(short);
  }
  if (yes.length === 0 && call.length === 0) return "None authorized";
  const parts: string[] = [];
  if (yes.length) parts.push(`Yes: ${yes.join(", ")}`);
  if (call.length) parts.push(`Call First: ${call.join(", ")}`);
  if (no.length) parts.push(`No: ${no.join(", ")}`);
  return parts.join(" | ");
}

/** Upserts one per-item selection into an OtcAuthorization's selections array. */
export function setOtcSelection(
  selections: OtcSelection[],
  id: string,
  permission: OtcPermission
): OtcSelection[] {
  const rest = selections.filter((s) => s.id !== id);
  return [...rest, { id, permission }];
}
